import type { FilterOption } from '../filters/config/types';
import type { FilterRule, ListConfiguration } from '../list-builder/types';
import { containsDynamicDate } from '../utils/dynamic-date';

/**
 * Re-inject dynamic date placeholders into a resolved filter state before it is
 * persisted to a saved search (PD-14159).
 *
 * Bundled lists express relative date defaults with dynamic placeholders, e.g.
 * `invoiceDateRange` → `{ id: 'last-90-days', data: { startDate: '{{DATE:-90d}}',
 * endDate: '{{DATE:0d}}' } }`. When a list loads, `FilterRulesService.process()`
 * resolves those placeholders into concrete dates so the date picker can render
 * them. If we persisted that resolved state verbatim when copying or updating a
 * list, the saved search would freeze the range at save time — a "Last 90 Days"
 * copy made on 6/4 would forever read 3/6–6/4 instead of tracking today.
 *
 * The fix: before saving, walk the filter state and — for every value that still
 * matches its source-config default — restore the source default's ORIGINAL
 * placeholder `data`. The match is keyed on the FilterOption `id`, which survives
 * resolution untouched (only `data.startDate`/`endDate` get resolved). The
 * recovered placeholders then re-resolve fresh on every load via the normal
 * saved-search → ListConfiguration → process() pipeline.
 *
 * Left untouched:
 * - User-picked ranges — selecting dates in the picker sets `id: 'custom'`, which
 *   never matches a named default, so deliberate static ranges are preserved.
 * - Non-date filters (e.g. a salesperson `{ id: 'ME' }`) — their default `data`
 *   carries no dynamic placeholder, so nothing is swapped. The `ME` token needs
 *   no restoring either: the list builder keeps it as a token in filter state
 *   and resolves it to the viewer's id only when building a request (PD-15272),
 *   so it reaches this function — and the saved search — as `ME`. (Before that
 *   change the list builder resolved it on load, and saves froze the saver's
 *   own id; RestoreMeTokenInSavedListFilters repairs those rows.)
 * - Filters absent from the source config's `filterRules`.
 *
 * @param filterState  The resolved live filter state about to be persisted.
 * @param sourceConfig The list configuration the filter state was derived from
 *                     (its `filterRules` still hold the original placeholders).
 * @returns A new filter state with dynamic placeholders restored where matched.
 *          The input is never mutated.
 */
export function preserveDynamicDates(
	filterState: Record<string, unknown>,
	sourceConfig: Pick<ListConfiguration, 'filterRules'> | null | undefined,
): Record<string, unknown> {
	const filterRules = sourceConfig?.filterRules;

	if (filterRules == null) {
		return filterState;
	}

	const result: Record<string, unknown> = { ...filterState };

	for (const [filterKey, value] of Object.entries(filterState)) {
		const rule: FilterRule | undefined = filterRules[filterKey];
		const defaultValue = rule?.defaultValue;

		// We only re-dynamize array-of-FilterOption defaults (date-range filters).
		// A string defaultValue or a non-array live value can't carry the
		// {startDate, endDate} placeholder shape we're restoring.
		if (!Array.isArray(value) || !Array.isArray(defaultValue)) {
			continue;
		}

		// The live value's element type is unknown (it round-tripped through the
		// store as Record<string, unknown>); narrow each element at runtime.
		const liveOptions: Array<unknown> = value;
		const defaults: Array<FilterOption> = defaultValue;

		const nextValue = liveOptions.map((option): unknown => {
			if (option == null || typeof option !== 'object') {
				return option;
			}

			const opt = option as FilterOption;

			// Match the source default by id — the id is preserved through
			// resolution, so an untouched "Last 90 Days" value still reports
			// id 'last-90-days' even though its data was resolved to concrete dates.
			const match = defaults.find((d) => d.id === opt.id);
			const sourceData = match?.data as
				| { startDate?: unknown; endDate?: unknown }
				| undefined;

			if (sourceData == null) {
				return option;
			}

			// Only restore when the source default actually carries a dynamic
			// placeholder — otherwise there's nothing relative to preserve.
			const sourceHasDynamic =
				(typeof sourceData.startDate === 'string' &&
					containsDynamicDate(sourceData.startDate)) ||
				(typeof sourceData.endDate === 'string' &&
					containsDynamicDate(sourceData.endDate));

			if (!sourceHasDynamic) {
				return option;
			}

			// Keep id/label/etc.; swap only the resolved data back to the
			// original placeholder data (cloned so the source config is untouched).
			return { ...opt, data: { ...sourceData } };
		});

		// Only replace the filter when at least one option was re-dynamized
		// (unchanged options are returned by-reference from the map above).
		const changed = nextValue.some((next, i) => next !== liveOptions[i]);

		if (changed) {
			result[filterKey] = nextValue;
		}
	}

	return result;
}
